// SEO-помощники, общие для шлюза (страницы для роботов, sitemap) и витрины
// (ЧПУ товара, вставка мета-тегов в оболочку SPA). Без зависимостей: модуль
// работает и в Bun-сервере, и в браузерном бандле.

const TRANSLIT: Record<string, string> = {
	а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i",
	й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t",
	у: "u", ф: "f", х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "shch", ъ: "", ы: "y", ь: "",
	э: "e", ю: "yu", я: "ya",
};

/** Человекочитаемая часть адреса: латиница, цифры и одиночные дефисы, до 60 символов. */
export const slugify = (text: string, maxLength = 60): string => {
	const latin = [...text.toLowerCase()].map((ch) => TRANSLIT[ch] ?? ch).join("");
	const slug = latin
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
	if (slug.length <= maxLength) return slug;
	const cut = slug.slice(0, maxLength);
	const lastDash = cut.lastIndexOf("-");
	return (lastDash > maxLength / 2 ? cut.slice(0, lastDash) : cut).replace(/-+$/g, "");
};

// Форматы id товара: сидовые `prod-123` и выданные шлюзом `prd_<16 hex>`
// (newId("prd")). ЧПУ строится только для них — так id всегда однозначно
// отрезается от хвоста адреса. Товар с id другого вида живёт по `/product/<id>`.
const PRODUCT_ID_TAIL = /(?:^|-)((?:prod-\d+)|(?:prd_[0-9a-z]+))$/i;

const isSlugCompatibleId = (id: string): boolean => {
	const m = PRODUCT_ID_TAIL.exec(id);
	return Boolean(m && m[1] === id);
};

/** Сегмент ЧПУ товара: `<slug>-<id>`; без названия или для нестандартного id — просто id. */
export const productSegment = (id: string, name?: string | null): string => {
	if (!isSlugCompatibleId(id)) return id;
	const slug = name ? slugify(name) : "";
	return slug ? `${slug}-${id}` : id;
};

/** Канонический путь страницы товара: `/product/<slug>-<id>`. */
export const productPath = (id: string, name?: string | null): string =>
	`/product/${encodeURIComponent(productSegment(id, name))}`;

/**
 * id товара из сегмента адреса. Понимает и ЧПУ (`/product/<slug>-<id>`), и
 * старые ссылки (`/product/<id>`): если хвост не похож на id, сегмент — сам id.
 */
export const productIdFromParam = (segment: string): string => {
	let decoded = segment;
	try {
		decoded = decodeURIComponent(segment);
	} catch {
		// оставляем как есть
	}
	const m = PRODUCT_ID_TAIL.exec(decoded);
	return m?.[1] ?? decoded;
};

// Поисковые и «превью» роботы: им отдаётся HTML с содержимым страницы, а не
// пустая оболочка SPA. Остальным — та же оболочка с мета-тегами.
const BOT_UA =
	/(yandex|googlebot|google-inspectiontool|googleother|adsbot-google|mediapartners-google|bingbot|duckduckbot|baiduspider|slurp|mail\.ru_bot|petalbot|applebot|facebookexternalhit|vkshare|telegrambot|twitterbot|whatsapp|linkedinbot|skypeuripreview|discordbot)/i;

export const isSearchBot = (userAgent: string | null | undefined): boolean =>
	Boolean(userAgent && BOT_UA.test(userAgent));

export const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

/** JSON для `<script type="application/ld+json">`: `</script>` внутри строки не закроет тег. */
export const jsonLdScript = (data: unknown): string =>
	`<script type="application/ld+json">${JSON.stringify(data).replace(/</g, "\\u003c")}</script>`;

/**
 * Вставить мета-теги страницы в оболочку SPA (`index.html`).
 *
 * Из оболочки убираются общие `<title>`, `description` и `canonical`, перед
 * `</head>` встаёт `head` страницы. `body` (для роботов) кладётся внутрь
 * `<div id="root">` — React при монтировании заменит его приложением.
 */
export const injectSeo = (shell: string, parts: { head: string; body?: string; noscript?: string }): string => {
	let html = shell
		.replace(/<title>[\s\S]*?<\/title>\s*/i, "")
		.replace(/<meta\s+name=["']description["'][^>]*>\s*/i, "")
		.replace(/<link\s+rel=["']canonical["'][^>]*>\s*/i, "");
	html = html.includes("</head>") ? html.replace("</head>", `${parts.head}\n</head>`) : `${parts.head}${html}`;
	if (parts.body) {
		html = html.replace(/<div id=["']root["']>\s*<\/div>/i, `<div id="root">${parts.body}</div>`);
	} else if (parts.noscript) {
		html = html.replace(/(<div id=["']root["']>\s*<\/div>)/i, `$1<noscript>${parts.noscript}</noscript>`);
	}
	return html;
};
